import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { CategoryEntity, selectCategoryId } from '@frontend/data-access-categories';
import { selectCategoryTreeCategory, CategoryTreeEntity } from '@frontend/data-access-category-tree';
import { filter, Observable, Subscription } from 'rxjs';
import { selectLanguage } from '@frontend/data-access-general';
import { ActivatedRoute, Router, RouterLink, NavigationEnd } from '@angular/router';
import { LocalizeRouterPipe } from '@frontend/util-router-history';
import { DocumentDetailsEntity, selectDocumentDetails } from '@frontend/data-access-document-details';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';

interface BreadcrumbItem extends CategoryTreeEntity {
  path: string;
}
@Component({
  selector: 'dc-breadcrumb',
  standalone: true,
  imports: [RouterLink, LocalizeRouterPipe],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private titleService = inject(Title);

  categories: CategoryEntity[] = [];
  breadcrumbs: BreadcrumbItem[] = [];
  visibleBreadcrumbs: BreadcrumbItem[] = [];
  showEllipsis = false;
  pimcoreCategoryTree$: Observable<CategoryTreeEntity[]>;
  categoryTreeSubscription!: Subscription;
  categoryTree: CategoryTreeEntity[] = [];
  categoryIdSubscription!: Subscription;
  categoryId$: Observable<number | undefined>;
  language$: Observable<string | null>;
  activeLang = 'de';
  currentCategoryId: number | undefined = undefined;
  documentDetails: DocumentDetailsEntity | null = null;
  documentDetails$: Observable<DocumentDetailsEntity | null>;
  documentDetailsSubscription!: Subscription;
  isSearchPath = false;
  isFavoritesPath = false;

  constructor() {
    this.checkScreenSize();
    this.pimcoreCategoryTree$ = this.store.pipe(select(selectCategoryTreeCategory));
    this.categoryId$ = this.store.pipe(select(selectCategoryId));
    this.language$ = this.store.pipe(select(selectLanguage));
    this.documentDetails$ = this.store.pipe(select(selectDocumentDetails));
  }

  ngOnInit() {
    this.categoryTreeSubscription = this.pimcoreCategoryTree$.subscribe((categoryTree) => {
      this.categoryTree = categoryTree;
      this.updateBreadcrumbs();
    });

    this.categoryIdSubscription = this.categoryId$.subscribe((categoryId) => {
      if (this.categoryTree) {
        this.currentCategoryId = categoryId;
        this.updateBreadcrumbs();
      }
    });

    this.documentDetailsSubscription = this.documentDetails$.subscribe((_doc) => {
      this.documentDetails = _doc;
      this.updateBreadcrumbs();
    });

    if (this.router.url.includes('/search')) {
      this.isSearchPath = true;
      this.isFavoritesPath = false;
      this.updateBreadcrumbs();
    }

    if (this.router.url.includes('/favorites')) {
      this.isFavoritesPath = true;
      this.isSearchPath = false;
      this.updateBreadcrumbs();
    }

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (event.urlAfterRedirects.includes('/search')) {
        this.isSearchPath = true;
        this.isFavoritesPath = false;
      } else if (event.urlAfterRedirects.includes('/favorites')) {
        this.isFavoritesPath = true;
        this.isSearchPath = false;
      } else {
        this.isSearchPath = false;
        this.isFavoritesPath = false;
      }
      this.updateBreadcrumbs();
    });
  }

  updateBreadcrumbs() {
    if (this.isSearchPath) {
      const searchBreadcrumb: BreadcrumbItem = {
        id: 0,
        name: this.translate.instant('SEARCH_RESULTS'),
        image: '',
        has_documents: false,
        has_disclaimer: false,
        path: '',
      };

      this.breadcrumbs = [];
      this.breadcrumbs.push(searchBreadcrumb);
    } else if (this.isFavoritesPath) {
      const favoritesBreadcrumb: BreadcrumbItem = {
        id: 0,
        name: this.translate.instant('FAVORITES'),
        image: '',
        has_documents: false,
        has_disclaimer: false,
        path: '',
      };

      this.breadcrumbs = [];
      this.breadcrumbs.push(favoritesBreadcrumb);
    } else if (this.categoryTree && this.currentCategoryId) {
      this.breadcrumbs = this.buildBreadcrumb(this.categoryTree, this.currentCategoryId);
    } else {
      this.breadcrumbs = [];
    }

    this.visibleBreadcrumbs = this.breadcrumbs;
    this.updateBreadcrumbsView();

    if (this.breadcrumbs.length > 0) {
      this.titleService.setTitle(this.breadcrumbs[this.breadcrumbs.length - 1].name);
    }
  }

  ngOnDestroy() {
    if (this.categoryTreeSubscription) {
      this.categoryTreeSubscription.unsubscribe();
    }
    if (this.categoryIdSubscription) {
      this.categoryIdSubscription.unsubscribe();
    }
    if (this.documentDetailsSubscription) {
      this.documentDetailsSubscription.unsubscribe();
    }
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.updateBreadcrumbsView();
  }

  private buildBreadcrumb(data: CategoryTreeEntity[], targetId: number): BreadcrumbItem[] {
    const path: BreadcrumbItem[] = [];
    const documentDetails = this.documentDetails;
    const docId = Number(this.route.snapshot.paramMap.get('documentId'));

    function traverse(node: CategoryTreeEntity, ancestors: BreadcrumbItem[]): boolean {
      // Create a CategoryEntity from the current node
      const entity: BreadcrumbItem = {
        id: node.id,
        name: node.name,
        image: '',
        has_documents: node.has_documents,
        has_disclaimer: node.has_disclaimer,
        path: '/categories/' + node.id + (node.has_documents ? '/documents' : ''),
      };

      if (node.id === targetId) {
        // Add current entity to the ancestors array and to the path
        ancestors.push(entity);
        path.push(...ancestors);

        if (documentDetails) {
          const documentBreadcrumb: BreadcrumbItem = {
            id: docId,
            name: documentDetails.title,
            image: '',
            has_documents: false,
            has_disclaimer: false,
            path: '',
          };
          path.push(documentBreadcrumb);
        }
        return true;
      }

      if (node.childcategories) {
        for (const subLink of node.childcategories) {
          if (traverse(subLink, [...ancestors, entity])) {
            return true;
          }
        }
      }
      return false;
    }

    for (const subLink of data) {
      traverse(subLink, []);
    }

    //this.documentDetails = null;
    return path;
  }

  updateBreadcrumbsView() {
    const container = document.querySelector('.breadcrumb-container-left-content');
    if (!container) return;

    const availableWidth = container.clientWidth;
    let totalWidth = 0;
    let visibleCount = 0;

    // Reset breadcrumbs
    this.showEllipsis = false;
    this.visibleBreadcrumbs = this.breadcrumbs;

    for (let i = this.breadcrumbs.length - 1; i >= 0; i--) {
      const breadcrumb = this.breadcrumbs[i];
      const element = document.createElement('span');
      element.style.visibility = 'hidden';
      element.style.position = 'absolute';
      element.style.fontSize = '0.75rem';
      element.className = 'breadcrumb-item-title breadcrumb-item-title-not-active';
      element.innerHTML = breadcrumb.name;
      container.appendChild(element);

      const elementWidth = element.clientWidth + 42; // 42 für Paddings/Margins
      totalWidth += elementWidth;

      container.removeChild(element);

      if (totalWidth <= availableWidth) {
        visibleCount++;
      } else {
        break;
      }
    }

    if (visibleCount < this.breadcrumbs.length) {
      this.showEllipsis = true;
      this.visibleBreadcrumbs = this.breadcrumbs.slice(-visibleCount);
    }
  }
}
